import { useEffect, useMemo, useState } from 'react'

import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { DeepMap, FieldValues } from 'react-hook-form'

import {
  useDeviceCameraEditQuery,
  useFloorsQuery,
  useUpdateCameraMutation,
  useUpdateDeviceMutation,
} from '@/graphql/generated/hooks'

import { UpdateCameraInputsI, UpdateDeviceInputsI } from '../../types/types'
import { getFloorOptions } from '../../utils/utils'
import {
  isDeviceFieldsDirty,
  isLiveFieldsDirty,
  isLiveStreamingSetup,
  shouldClearCredentials,
} from '../utils/utils'

export const DEFAULT_MASKED_PASSWORD = 'DEFAULT_PASSWORD'

export const useEditCamera = (deviceId: string) => {
  const { isOrangePeel } = useLiveStreamSetup()
  const [shouldShowLiveStreamFields, setShowLiveStreamFields] = useState(false)
  const { data: floorsData, loading: isFloorsLoading } = useFloorsQuery({
    fetchPolicy: 'network-only',
  })
  const {
    data: cameraData,
    loading: isCameraLoading,
    error: cameraError,
  } = useDeviceCameraEditQuery({
    variables: {
      id: deviceId,
    },
    fetchPolicy: 'network-only',
  })
  const [updateDevice, { loading: isUpdateDeviceLoading }] =
    useUpdateDeviceMutation()

  const [updateCameraDevice, { loading: isUpdateCameraLoading }] =
    useUpdateCameraMutation()

  const floorsOptions = useMemo(() => getFloorOptions(floorsData), [floorsData])

  useEffect(() => {
    if (cameraData) {
      setShowLiveStreamFields(isLiveStreamingSetup(cameraData, isOrangePeel))
    }
  }, [cameraData])

  const handleSetLiveStreamFields = () => {
    setShowLiveStreamFields(true)
  }

  const update = async (formData: UpdateDeviceInputsI) => {
    await updateDevice({
      variables: {
        input: formData,
      },
      refetchQueries: ['Devices', 'DeviceCameraEdit'],
    })
  }

  const updateCamera = async (
    formData: UpdateDeviceInputsI,
    cameraData: UpdateCameraInputsI,
    dirtyFields: DeepMap<FieldValues, true>
  ) => {
    // If a camera does not require Authentication, clear username/password only if neither is provided
    if (shouldClearCredentials(dirtyFields, cameraData)) {
      cameraData.rtspPassword = null
      cameraData.rtspUsername = null
    }

    if (isDeviceFieldsDirty(dirtyFields)) {
      await update(formData)
    }
    if (isLiveFieldsDirty(dirtyFields)) {
      await updateCameraDevice({
        variables: {
          input: cameraData,
        },
        refetchQueries: ['DeviceCameraEdit'],
      })
    }
  }

  return {
    shouldShowLiveStreamFields,
    handleSetLiveStreamFields,
    floorsOptions,
    cameraData,
    cameraError,
    isLoading: isCameraLoading || isFloorsLoading,
    isUpdateDeviceLoading,
    isUpdateCameraLoading,
    update,
    updateCamera,
  }
}
